import styled from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";

/**
 * Container fills its grid cell width. The actual width of the secondary
 * sidebar is now controlled by `SECONDARY_SIDEBAR_WIDTH` in
 * ResponsiveLayout.tsx (a percentage of the layout). Tweak there.
 */
export const Container = styled.aside`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${spacing[3]} 0;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.surface.primary};
  border: ${({ theme }) => theme.borders.width[1]} ${({ theme }) =>
    theme.borders.style.solid} ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  box-sizing: border-box;
  overflow: hidden;
`;

/**
 * Breadcrumb has been moved to ResponsiveLayout's `pageheader` grid row so
 * it can span both the secondary sidebar and main content columns. Kept
 * here as a styled export in case any other consumer still imports it.
 */
export const BreadcrumbRow = styled.nav`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[3]} ${spacing[3]};
  box-sizing: border-box;

  a {
    color: ${({ theme }) => theme.colors.text.interactive};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: ${spacing[1]};
  }

  a:hover {
    text-decoration: underline;
  }

  > span[aria-current] {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/**
 * Sidebar section whose bottom edge acts as the divider between sections.
 * Keeping the divider as a layout concern means the dropdown trigger and
 * the add-instance button stay visually clean (no internal borders).
 */
export const SectionRow = styled.div`
  padding: 0 ${spacing[3]};
  border-bottom: ${({ theme }) => theme.borders.width[1]} ${({ theme }) =>
    theme.borders.style.solid} ${({ theme }) => theme.colors.border.subtle};
`;

export const AddInstanceRow = styled(SectionRow)`
  display: flex;
  justify-content: center;
  padding: ${spacing[2]} ${spacing[3]};
`;

export const AddInstanceButton = styled.button`
  ${typographyMixin("caption", 3)}
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  background: transparent;
  border: 0;
  padding: ${spacing[2]};
  color: ${({ theme }) => theme.colors.text.interactive};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Sits flush against the AddInstanceRow's bottom divider — no top padding,
 * no parent gap — so the first instance row appears to attach directly to
 * the divider line.
 */
export const InstanceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const EmptyState = styled.p`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin: 0;
  padding: ${spacing[2]} ${spacing[3]};
`;
